// `uib-tooltip` / `uib-tooltip-template` with `tooltip-append-to-body`, as pure geometry.
//
// Upstream's tooltips are angular-ui-bootstrap's, and what they draw is bootstrap's own
// markup, appended to `<body>`:
//
//   <div class="tooltip fade top top-right in" style="top: 783px; left: 659px">
//     <div class="tooltip-arrow" style="inset: auto 3px 0px auto; margin: 0px"></div>
//     <div class="tooltip-inner">…</div>
//   </div>
//
// That is measured, not recalled — the disabled Send's tooltip on the live dev tenant. The
// vendored `_tooltip.scss` styles every part of it (`.tooltip.in { opacity: .9 }`, the 200px
// `.tooltip-inner`, the arrow's border triangle per placement), so getting the CLASSES right
// is most of the port; the rest is `$uibPosition.positionElements` and `positionArrow`,
// which are here.
//
// ⚠️ THE NUMBERS ARE INTEGERS ON PURPOSE. uib rounds the host's offset (`Math.round` on the
// bounding rect plus the page scroll) and measures the popup by `offsetWidth`/`offsetHeight`,
// which are already integers — so a 49.59px-tall tooltip is placed as if it were 50. Using
// the fractional rect instead moves it by a pixel against production.

/** `[primary, secondary]` — uib's `parsePlacement`, minus the `auto` prefix nobody here uses. */
export type UibPlacement = 'top' | 'bottom' | 'left' | 'right' | `${'top' | 'bottom'}-${'left' | 'right'}` | `${'left' | 'right'}-${'top' | 'bottom'}`

export function parsePlacement(placement: UibPlacement): [string, string] {
  const [primary = 'top', secondary = 'center'] = placement.split('-')
  return [primary, secondary]
}

/**
 * The popup's class list in uib's order: `tooltip`, the `tooltip-class`, `fade`, the primary
 * placement, then — only for a two-part placement — the whole placement as a class
 * (`placementClassPrefix + placement`), and `in` once it has opened.
 *
 * ⚠️ `top-right` IS BOTH `top` AND `top-right`. The vendored stylesheet keys the margin and
 * padding off `.tooltip.top` and the arrow's corner off `.tooltip.top-right`; either alone
 * draws a different tooltip.
 */
export function uibTooltipClass(placement: UibPlacement, popupClass: string | undefined, isIn: boolean): string {
  const [primary, secondary] = parsePlacement(placement)
  return [
    'tooltip',
    popupClass ?? '',
    'fade',
    primary,
    secondary === 'center' ? '' : placement,
    isIn ? 'in' : '',
  ]
    .filter(Boolean)
    .join(' ')
}

export interface HostOffset {
  top: number
  left: number
  width: number
  height: number
}

/** `$uibPosition.offset` — the host's page coordinates, each rounded. */
export function hostOffset(rect: { top: number; left: number; width: number; height: number }, scrollX: number, scrollY: number): HostOffset {
  return {
    width: Math.round(rect.width),
    height: Math.round(rect.height),
    top: Math.round(rect.top + scrollY),
    left: Math.round(rect.left + scrollX),
  }
}

/**
 * `$uibPosition.positionElements(host, target, placement, appendToBody=true)` — where the
 * popup's top-left goes, in page coordinates.
 */
export function positionElements(host: HostOffset, target: { width: number; height: number }, placement: UibPlacement): { top: number; left: number } {
  const [primary, secondary] = parsePlacement(placement)
  let top = 0
  let left = 0
  switch (primary) {
    case 'top':
      top = host.top - target.height
      break
    case 'bottom':
      top = host.top + host.height
      break
    case 'left':
      left = host.left - target.width
      break
    case 'right':
      left = host.left + host.width
      break
    default:
      break
  }
  const vertical = primary === 'top' || primary === 'bottom'
  switch (secondary) {
    case 'top':
      top = host.top
      break
    case 'bottom':
      top = host.top + host.height - target.height
      break
    case 'left':
      left = host.left
      break
    case 'right':
      left = host.left + host.width - target.width
      break
    default:
      if (vertical) left = host.left + host.width / 2 - target.width / 2
      else top = host.top + host.height / 2 - target.height / 2
      break
  }
  return { top: Math.round(top), left: Math.round(left) }
}

/**
 * `$uibPosition.positionArrow` — the arrow's inline style, or `undefined` for a centred
 * placement, where uib clears it and the stylesheet's own `left: 50%` stands.
 *
 * For a corner placement the arrow sits flush on the primary edge and inset from the
 * secondary one by the `.tooltip-inner`'s border radius on that corner — 3px with the
 * vendored theme, which is the `right: 3px` measured on production.
 */
export function arrowStyle(placement: UibPlacement, borderRadius: string): Record<string, string> | undefined {
  const [primary, secondary] = parsePlacement(placement)
  if (secondary === 'center') return undefined
  const style: Record<string, string> = { top: 'auto', right: 'auto', bottom: 'auto', left: 'auto', margin: '0px' }
  const flush: Record<string, string> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' }
  const edge = flush[primary]
  if (edge !== undefined) style[edge] = '0px'
  style[secondary] = borderRadius
  return style
}
